import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../modules/icon";
import "./DocumentTags.style.css";

interface DocumentTagsProps {
  availableTags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  loading?: boolean;
}

export function DocumentTags({
  availableTags,
  selectedTags,
  onTagsChange,
  loading = false,
}: DocumentTagsProps) {
  const { t } = useTranslation();

  const handleTagToggle = (tag: string) => {
    const isSelected = selectedTags.includes(tag);
    if (isSelected) {
      onTagsChange(selectedTags.filter((t) => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const handleRemoveTag = (tag: string) => {
    onTagsChange(selectedTags.filter((t) => t !== tag));
  };

  if (loading) {
    return null;
  }

  return (
    <div className="document-tags-container mt-3">
      <label className="form-label">
        {t("DOCUMENT_TAGS.LABEL", "Tags")}
      </label>

      {/* Available Tags Section */}
      {availableTags.length > 0 && (
        <div className="available-tags">
          <div className="tags-section-header">
            <small className="text-muted">
              {t("DOCUMENT_TAGS.AVAILABLE", "Add")}
            </small>
          </div>
          <div className="tags-list">
            {availableTags.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  className={`tag-badge available ${
                    isSelected ? "selected" : ""
                  } ${isSelected ? "disabled" : ""}`}
                  onClick={() => !isSelected && handleTagToggle(tag)}
                  disabled={isSelected}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Tags Section */}
      {selectedTags.length > 0 && (
        <div className="selected-tags">
          <div className="tags-section-header">
            <small className="text-muted">
              {t("DOCUMENT_TAGS.SELECTED", "Document Tags")}
            </small>
          </div>
          <div className="tags-list">
            {selectedTags.map((tag) => (
              <div key={tag} className="tag-badge selected">
                <span>{tag}</span>
                <button
                  type="button"
                  className="tag-remove-btn"
                  onClick={() => handleRemoveTag(tag)}
                  aria-label={`Remove tag ${tag}`}
                >
                  <Icon type="fa-solid fa-xmark" size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {availableTags.length === 0 && selectedTags.length === 0 && (
        <div className="alert alert-info mt-2" role="alert">
          {t("DOCUMENT_TAGS.NO_TAGS", "No tags available")}
        </div>
      )}
    </div>
  );
}
